/**
 * Work-form save failures, in words the technician can act on.
 *
 * A refused work form is usually refused for one nameable reason — a crew that
 * no longer exists, a required field the form let through — and the API says
 * which in the detail of its error envelope. Reporting every refusal as the
 * same sentence hides that reason behind a support ticket, so the detail is
 * what the toast leads with, and the generic sentence is only what is left
 * when the API sent nothing usable.
 *
 * Contains:
 * - createJobErrorMessage — the failure-toast message for a rejected save
 * - updateJobErrorMessage — the same, for a rejected edit of a saved form
 *
 * Returns plain JS — no JSX.
 */
import { parseApiError } from '@sophocles/utils/apiErrors';

// Details arrive one per offending field; a single toast line carries them all.
const DETAIL_SEPARATOR = '; ';

/**
 * The API's own account of why it refused a save, as one line.
 *
 * `knownFields: []` is the toast-only idiom: this surface renders no inputs of
 * its own, so a `"<field>: <message>"` detail stays whole rather than being
 * attributed to an input that would never show it.
 *
 * @param {*} error
 * @returns {string} The joined detail, or an empty string when the API sent
 *   nothing usable — the caller's cue to fall back to a generic sentence.
 */
function saveErrorDetail(error) {
  const { nonFieldMessages } = parseApiError(error, { knownFields: [], genericMessage: null });
  return nonFieldMessages.join(DETAIL_SEPARATOR);
}

/**
 * Build the failure-toast message for a work form the API refused to save.
 *
 * @param {*} error - The rejected save's error: an axios error, an RTK Query
 *   error, or a response body. Anything `parseApiError` accepts.
 * @param {function(string, string, object=): string} t - i18next `t`.
 * @returns {string} The message to show, never empty.
 */
export function createJobErrorMessage(error, t) {
  const detail = saveErrorDetail(error);

  if (detail === '') {
    return t('projectServices.workForm.failedToCreateJob', 'Failed to create job.');
  }

  return t('projectServices.workForm.failedToCreateJobDetail', 'Failed to create job: {{errors}}', {
    errors: detail,
  });
}

/**
 * Build the failure-toast message for an edit the API refused to apply to an
 * already-saved work form.
 *
 * @param {*} error - The rejected edit's error. Anything `parseApiError` accepts.
 * @param {function(string, string, object=): string} t - i18next `t`.
 * @returns {string} The message to show, never empty.
 */
export function updateJobErrorMessage(error, t) {
  const detail = saveErrorDetail(error);

  if (detail === '') {
    return t('projectServices.workForm.failedToUpdateJob', 'Failed to update job.');
  }

  return t('projectServices.workForm.failedToUpdateJobDetail', 'Failed to update job: {{errors}}', {
    errors: detail,
  });
}
